import React, { useEffect, useRef, useState } from "react";
import { X, PaperPlaneTilt, ChatCircleDots, TrashSimple } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { useAI } from "@/context/AIContext";
import { toast } from "sonner";

const QUICK_PROMPTS = [
  "¿Cuál es mi próximo paso?",
  "Revisa mi progreso actual",
  "Ayúdame a resolver un bloqueo",
  "Ideas de marketing para mi negocio",
  "Sugerencias de optimización",
];

function useSessionId() {
  const [sid] = useState(() => {
    let s = localStorage.getItem("ruta_ai_session");
    if (!s) {
      s = "sess-" + Math.random().toString(36).slice(2, 10);
      localStorage.setItem("ruta_ai_session", s);
    }
    return s;
  });
  return sid;
}

export default function AIDrawer() {
  const { open, closeDrawer, prefill, clearPrefill, context } = useAI();
  const sessionId = useSessionId();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    if (!open || loaded) return;
    api.get(`/ai/history/${sessionId}`)
      .then((r) => setMessages(r.data.messages || []))
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, [open, sessionId, loaded]);

  useEffect(() => {
    if (open && prefill) {
      setInput(prefill);
      clearPrefill();
    }
  }, [open, prefill, clearPrefill]);

  useEffect(() => {
    if (bottomRef.current) bottomRef.current.scrollIntoView({ behavior: "smooth" });
  }, [messages, sending]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || sending) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg, id: `local-${Date.now()}` }]);
    setSending(true);
    try {
      const { data } = await api.post("/ai/chat", { session_id: sessionId, message: msg, context });
      setMessages((m) => [...m, { role: "assistant", content: data.reply, id: `local-r-${Date.now()}` }]);
    } catch (e) {
      toast.error("No se pudo obtener respuesta. Intenta de nuevo.");
    } finally {
      setSending(false);
    }
  };

  const clear = async () => {
    try {
      await api.delete(`/ai/history/${sessionId}`);
      setMessages([]);
      toast.success("Historial limpio");
    } catch {
      toast.error("No se pudo limpiar el historial");
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="ai-drawer">
      <div
        className="flex-1 bg-black/40 backdrop-blur-[2px]"
        onClick={closeDrawer}
        data-testid="ai-drawer-overlay"
      />
      <aside
        className="w-full max-w-[440px] h-full bg-background border-l-2 border-foreground flex flex-col animate-slide-in-right"
      >
        <div className="flex items-center justify-between p-5 border-b-2 border-foreground">
          <div className="flex items-center gap-2">
            <ChatCircleDots size={20} weight="fill" />
            <div className="label-mono">Obtener Ayuda</div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={clear} className="btn-ghost" data-testid="btn-clear-ai" title="Limpiar historial">
              <TrashSimple size={16} weight="bold" />
            </button>
            <button onClick={closeDrawer} className="btn-ghost" data-testid="btn-close-ai" title="Cerrar">
              <X size={18} weight="bold" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4" data-testid="ai-messages">
          {messages.length === 0 && !sending && (
            <div className="space-y-4">
              <div className="brut-card">
                <div className="label-mono text-muted-foreground">Cómo puedo ayudarte</div>
                <p className="mt-2 text-sm leading-relaxed">
                  Estoy aquí para responder <em>¿cuál es el siguiente paso?</em>. Pregúntame por tu playbook,
                  bloqueos, ideas de marketing o próximos hitos.
                </p>
              </div>
              <div>
                <div className="label-mono mb-2 text-muted-foreground">Sugerencias</div>
                <div className="flex flex-col gap-2">
                  {QUICK_PROMPTS.map((p, i) => (
                    <button
                      key={i}
                      onClick={() => send(p)}
                      data-testid={`ai-quick-prompt-${i}`}
                      className="text-left border-2 border-foreground px-3 py-2 hover:bg-foreground hover:text-background transition-colors text-sm"
                    >
                      {p}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {messages.map((m, idx) => (
            <div
              key={m.id || idx}
              data-testid={`ai-message-${m.role}`}
              className={`border-2 border-foreground p-3 ${
                m.role === "user" ? "ml-8 bg-foreground text-background" : "mr-8 bg-surface"
              }`}
            >
              <div className="label-mono mb-1 opacity-70">
                {m.role === "user" ? "Tú" : "Ruta"}
              </div>
              <div className="text-sm whitespace-pre-wrap leading-relaxed">{m.content}</div>
            </div>
          ))}
          {sending && (
            <div className="mr-8 border-2 border-foreground p-3 bg-surface" data-testid="ai-typing">
              <div className="label-mono opacity-70 mb-1">Ruta</div>
              <div className="text-sm opacity-60">Escribiendo…</div>
            </div>
          )}
          <div ref={bottomRef} />
        </div>

        <div className="p-4 border-t-2 border-foreground">
          <form
            onSubmit={(e) => { e.preventDefault(); send(); }}
            className="flex items-stretch gap-2"
          >
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Escribe tu pregunta…"
              className="input-brut flex-1"
              data-testid="ai-input"
              disabled={sending}
            />
            <button
              type="submit"
              className="btn-primary flex items-center gap-2"
              data-testid="btn-send-ai"
              disabled={sending || !input.trim()}
            >
              <PaperPlaneTilt size={16} weight="bold" />
            </button>
          </form>
        </div>
      </aside>
    </div>
  );
}
